/*
  Eva Radio: bass-reactive speaker arcs + hand-drawn floating note glyphs,
  plus hand-drawn hover symbols over the transport buttons. Pure decorative
  overlay -- the boombox photo itself never moves. Independent file from
  eva-radio.js (which owns playback/state); this one only listens for the
  'eva:radio:playstate' event that file already dispatches and reads the
  #EvaRadioAudio element directly.

  Everything here is drawn as jittered hand-line/oval strokes on a single
  <canvas> layered over the photo -- never font characters, never a smooth
  tween for the linework itself (see TICK_MS below). Position/opacity DO
  tween smoothly frame to frame; only the wobble redraws on a slow tick,
  which is deliberately the style, not a performance compromise.
*/
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  var REDUCED_MOTION = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  // Per angle, every position/size constant is measured in THAT photo's
  // own source pixels, then scaled by (renderedWidth / srcW) at draw
  // time. That one scale factor is applied uniformly to x and y, which
  // is what keeps the speaker arcs circular regardless of how wide the
  // widget currently is (resize feature) -- a % ellipse against the
  // widget's own non-square aspect ratio would NOT stay circular.
  //
  // 'above' speaker positions were derived the same way as its hotspot
  // polygons (see snippets/eva-radio.liquid): fit a perspective
  // transform from the front photo's door rectangle to the above
  // photo's door quadrilateral, then ran that transform on front's own
  // speaker centers -- verified against the actual photo, not eyeballed.
  // strip x1/x2 read directly off the above hotspot polygons' own
  // percentages (converted to that photo's pixel space).
  var GEOMETRY = {
    front: {
      srcW: 1901,
      srcH: 1057,
      speakerLeft: { cx: 417, cy: 674 },
      speakerRight: { cx: 1410, cy: 674 },
      speakerR: 170, // hugs the visible cone silhouette, not the whole grille panel
      // Must mirror the hotspot polygons in snippets/eva-radio.liquid --
      // kept here as plain constants (not read from the SVG) since the
      // hover symbol only needs each strip's horizontal center, not the
      // full polygon.
      strips: {
        play: { x1: 792.5, x2: 857.5 },
        rew: { x1: 857.5, x2: 922.5 },
        ff: { x1: 922.5, x2: 987.5 },
        stop: { x1: 987.5, x2: 1052.5 },
        pause: { x1: 1052.5, x2: 1117.5 }
      },
      hoverY: 1000 // in the blank margin below the chassis (photo ends ~945), not over the busy panel
    },
    above: {
      srcW: 2117,
      srcH: 955,
      speakerLeft: { cx: 509, cy: 679 },
      speakerRight: { cx: 1567, cy: 684 },
      speakerR: 181,
      strips: {
        play: { x1: 909.0, x2: 978.7 },
        rew: { x1: 978.7, x2: 1047.9 },
        ff: { x1: 1047.9, x2: 1117.1 },
        stop: { x1: 1117.1, x2: 1186.4 },
        pause: { x1: 1186.4, x2: 1255.6 }
      },
      hoverY: 830 // below the button row (bottom ~775px) -- above's photo has more blank margin under the chassis than front's
    }
  };

  var TICK_MS = 105; // ~9.5fps wobble-redraw cadence, per spec
  var MAX_NOTES = 2;
  var NOTE_LIFETIME_MS = 5000;
  var NOTE_RISE_PX_PER_S = 17;
  var HIT_REFRACTORY_MS = 120;
  var ARC_REST_OPACITY = 0.10;
  var ARC_HIT_OPACITY = 0.4;

  // --- Rough-sketch drawing primitives ------------------------------------

  function jitter(v, amt) { return v + (Math.random() * 2 - 1) * amt; }
  function randRange(a, b) { return a + Math.random() * (b - a); }

  // A "line" is a short jittered polyline (2-3 interior points), never a
  // single straight segment -- that's what reads as hand-drawn rather
  // than vector-clean.
  function line(x1, y1, x2, y2, amt, segments) {
    segments = segments || 3;
    var pts = [];
    for (var i = 0; i <= segments; i++) {
      var t = i / segments;
      pts.push([jitter(x1 + (x2 - x1) * t, amt), jitter(y1 + (y2 - y1) * t, amt)]);
    }
    return pts;
  }

  function ovalPts(cx, cy, rx, ry, amt, n) {
    n = n || 9;
    var pts = [];
    for (var i = 0; i < n; i++) {
      var a = (i / n) * Math.PI * 2;
      pts.push([jitter(cx + Math.cos(a) * rx, amt), jitter(cy + Math.sin(a) * ry, amt)]);
    }
    return pts;
  }

  function oval(cx, cy, rx, ry, amt, filled, n) {
    return { pts: ovalPts(cx, cy, rx, ry, amt, n), filled: !!filled };
  }

  function strokePointPath(c, pts, closed) {
    c.moveTo(pts[0][0], pts[0][1]);
    for (var i = 1; i < pts.length; i++) { c.lineTo(pts[i][0], pts[i][1]); }
    if (closed) { c.closePath(); }
  }

  // A glyph descriptor is { ovals: [{pts,filled}], lines: [[pt,pt,...]] },
  // always in ORIGIN-relative coordinates -- render() translates the
  // canvas to the glyph's current world position before drawing it, so
  // the same cached shape can be reused across many rAF frames while
  // only the position tweens.
  function renderGlyph(c, g) {
    g.ovals.forEach(function (o) {
      c.beginPath();
      strokePointPath(c, o.pts, true);
      if (o.filled) { c.fill(); } else { c.stroke(); }
    });
    g.lines.forEach(function (pts) {
      c.beginPath();
      strokePointPath(c, pts, false);
      c.stroke();
    });
  }

  // --- Note glyph library (11 variants, hand-jittered, never text) -------

  function glyphQuarter(cx, cy, s, amt) {
    return {
      ovals: [oval(cx, cy, s * 1.05, s * 0.75, amt * 0.6, true)],
      lines: [line(cx + s * 0.85, cy - s * 0.15, cx + s * 0.85, cy - s * 3.4, amt)]
    };
  }
  function glyphEighth(cx, cy, s, amt) {
    var g = glyphQuarter(cx, cy, s, amt);
    g.lines.push(line(cx + s * 0.85, cy - s * 3.4, cx + s * 1.9, cy - s * 2.6, amt));
    return g;
  }
  function glyphSixteenth(cx, cy, s, amt) {
    var g = glyphEighth(cx, cy, s, amt);
    g.lines.push(line(cx + s * 0.85, cy - s * 2.9, cx + s * 1.9, cy - s * 2.1, amt));
    return g;
  }
  function glyphHalf(cx, cy, s, amt) {
    return {
      ovals: [oval(cx, cy, s * 1.05, s * 0.75, amt * 0.6, false)],
      lines: [line(cx + s * 0.85, cy - s * 0.15, cx + s * 0.85, cy - s * 3.4, amt)]
    };
  }
  function glyphWhole(cx, cy, s, amt) {
    return { ovals: [oval(cx, cy, s * 1.2, s * 0.7, amt * 0.6, false)], lines: [] };
  }
  function glyphDottedQuarter(cx, cy, s, amt) {
    var g = glyphQuarter(cx, cy, s, amt);
    g.ovals.push(oval(cx + s * 1.7, cy, s * 0.22, s * 0.22, amt * 0.3, true, 6));
    return g;
  }
  function glyphBeamedEighthPair(cx, cy, s, amt) {
    var dx = s * 2.6, dy = s * 0.3;
    return {
      ovals: [
        oval(cx, cy, s * 1.0, s * 0.72, amt * 0.6, true),
        oval(cx + dx, cy + dy, s * 1.0, s * 0.72, amt * 0.6, true)
      ],
      lines: [
        line(cx + s * 0.8, cy - s * 0.1, cx + s * 0.8, cy - s * 3.3, amt),
        line(cx + dx + s * 0.8, cy + dy - s * 0.1, cx + dx + s * 0.8, cy + dy - s * 3.3, amt),
        line(cx + s * 0.8, cy - s * 3.3, cx + dx + s * 0.8, cy + dy - s * 3.3, amt, 2)
      ]
    };
  }
  function glyphBeamedTriplet(cx, cy, s, amt) {
    var dx = s * 2.1;
    var ovals = [], lines = [];
    var topY = cy - s * 3.0;
    for (var i = 0; i < 3; i++) {
      var hx = cx + dx * i, hy = cy + (i === 1 ? s * 0.2 : 0);
      ovals.push(oval(hx, hy, s * 0.85, s * 0.62, amt * 0.6, true, 8));
      lines.push(line(hx + s * 0.7, hy - s * 0.1, hx + s * 0.7, topY, amt));
    }
    lines.push(line(cx + s * 0.7, topY, cx + dx * 2 + s * 0.7, topY, amt, 2));
    return { ovals: ovals, lines: lines };
  }
  function glyphSharp(cx, cy, s, amt) {
    return {
      ovals: [],
      lines: [
        line(cx - s * 0.5, cy - s * 1.4, cx - s * 0.5, cy + s * 1.4, amt),
        line(cx + s * 0.5, cy - s * 1.5, cx + s * 0.5, cy + s * 1.3, amt),
        line(cx - s * 1.0, cy - s * 0.5, cx + s * 1.0, cy - s * 0.75, amt, 2),
        line(cx - s * 1.0, cy + s * 0.5, cx + s * 1.0, cy + s * 0.25, amt, 2)
      ]
    };
  }
  function glyphFlat(cx, cy, s, amt) {
    return {
      ovals: [oval(cx + s * 0.15, cy + s * 0.9, s * 0.55, s * 0.5, amt * 0.6, false, 7)],
      lines: [line(cx - s * 0.4, cy - s * 1.8, cx - s * 0.4, cy + s * 0.9, amt)]
    };
  }
  function glyphEighthRest(cx, cy, s, amt) {
    return {
      ovals: [oval(cx + s * 0.3, cy - s * 0.6, s * 0.35, s * 0.3, amt * 0.4, true, 6)],
      lines: [
        line(cx + s * 0.3, cy - s * 0.6, cx - s * 0.6, cy + s * 0.4, amt),
        line(cx - s * 0.6, cy + s * 0.4, cx + s * 0.4, cy + s * 1.5, amt)
      ]
    };
  }

  var NOTE_GLYPHS = [
    glyphQuarter, glyphEighth, glyphSixteenth, glyphHalf, glyphWhole,
    glyphDottedQuarter, glyphBeamedEighthPair, glyphBeamedTriplet,
    glyphSharp, glyphFlat, glyphEighthRest
  ];

  // --- Hover symbol library (play/stop/rew/ff/pause) ----------------------

  function hoverPlay(cx, cy, s, amt) {
    return {
      ovals: [],
      lines: [
        line(cx - s * 0.7, cy - s * 0.9, cx - s * 0.7, cy + s * 0.9, amt),
        line(cx - s * 0.7, cy + s * 0.9, cx + s * 0.9, cy, amt),
        line(cx + s * 0.9, cy, cx - s * 0.7, cy - s * 0.9, amt)
      ]
    };
  }
  function hoverStop(cx, cy, s, amt) {
    return {
      ovals: [],
      lines: [
        line(cx - s * 0.8, cy - s * 0.8, cx + s * 0.8, cy - s * 0.8, amt),
        line(cx + s * 0.8, cy - s * 0.8, cx + s * 0.8, cy + s * 0.8, amt),
        line(cx + s * 0.8, cy + s * 0.8, cx - s * 0.8, cy + s * 0.8, amt),
        line(cx - s * 0.8, cy + s * 0.8, cx - s * 0.8, cy - s * 0.8, amt)
      ]
    };
  }
  function hoverChevron(dir) {
    return function (cx, cy, s, amt) {
      var lines = [];
      for (var i = 0; i < 2; i++) {
        var ox = i * s * 0.7 * dir;
        lines.push(line(cx + ox - dir * s * 0.5, cy - s * 0.8, cx + ox + dir * s * 0.4, cy, amt));
        lines.push(line(cx + ox + dir * s * 0.4, cy, cx + ox - dir * s * 0.5, cy + s * 0.8, amt));
      }
      return { ovals: [], lines: lines };
    };
  }
  function hoverPause(cx, cy, s, amt) {
    return {
      ovals: [],
      lines: [
        line(cx - s * 0.5, cy - s * 0.9, cx - s * 0.5, cy + s * 0.9, amt),
        line(cx + s * 0.5, cy - s * 0.9, cx + s * 0.5, cy + s * 0.9, amt)
      ]
    };
  }

  var HOVER_GLYPHS = {
    play: hoverPlay,
    rew: hoverChevron(-1),
    ff: hoverChevron(1),
    stop: hoverStop,
    pause: hoverPause
  };

  // --- State ---------------------------------------------------------

  var dragHandle = null, img = null, audio = null, canvas = null, ctx = null;
  var dpr = Math.min(window.devicePixelRatio || 1, 2);

  var audioCtx = null, analyser = null, freqData = null, sourceNode = null;
  var vizAudio = null; // shadow element the analyser taps -- see initAudioGraph()'s own comment for why this is never the real #EvaRadioAudio element
  var playing = false;
  var currentAngle = 'front'; // eva-radio.js's angle changer -- arcs/notes/hover work on both front and above, GEOMETRY[currentAngle] is falsy for anything else
  var rafId = null, tickTimer = null;

  function currentGeometry() { return GEOMETRY[currentAngle] || null; }

  var bassSmoothed = 0;
  var arcPulse = 0;
  var lastHitAt = 0;
  var arcCache = null;

  var notes = [];

  var hoverAction = null;
  var hoverCache = null;

  function ink() {
    var v = getComputedStyle(document.documentElement).getPropertyValue('--colorTextBody');
    return (v && v.trim()) || '#1c1d1d';
  }

  // --- Canvas sizing ---------------------------------------------------

  function syncCanvasSize() {
    if (!canvas || !img) { return; }
    var w = img.clientWidth, h = img.clientHeight;
    if (!w || !h) { return; }
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
  }

  // --- WebAudio bass detection -----------------------------------------
  // FIX (2026-08-09, background-playback bug): this used to call
  // createMediaElementSource() directly on #EvaRadioAudio, the SAME
  // element eva-radio.js plays the actual tape music through. That
  // permanently reroutes an element's audio output through the Web Audio
  // graph for the life of the element (there's no way to "tap and also
  // keep the native output path" -- creating the source node hands
  // output control to the graph, full stop). iOS suspends AudioContexts
  // on backgrounding/lock -- well-documented WebKit behavior -- so once
  // tape audio's real output depended on this graph staying alive, tape
  // playback went silent on lock even though audio.paused stayed false
  // and the element kept "trying." The station stream never had this
  // problem because eva-radio-station.js's own file deliberately never
  // touches Web Audio at all (see that file's header comment) -- its
  // plain <audio> element's native output is untouched by anything,
  // which is exactly why it survives backgrounding.
  //
  // Fix: tap a SEPARATE, muted, shadow <audio> element instead of the
  // real one -- see ensureVizAudio()/syncVizAudio() below, which mirror
  // src/currentTime/play-pause onto it so the analyser still sees
  // (approximately) the same signal for the bass-reactive arcs/notes.
  // The real #EvaRadioAudio element is never passed to
  // createMediaElementSource() any more, so its output stays on its own
  // native, background-survivable path, same as the station.
  //
  // createMediaElementSource can only ever be called once per <audio>
  // element (throws on a second call), so this whole thing is guarded by
  // audioCtx being non-null. The analyser is deliberately NEVER connected
  // to audioCtx.destination -- getByteFrequencyData() only needs the
  // node to be IN the graph, not routed onward to speakers, so leaving it
  // a dead end guarantees the shadow element can never be audible
  // regardless of whether .muted is honored downstream (WebKit has a
  // documented history of NOT reliably respecting element.muted/.volume
  // once captured by Web Audio -- this sidesteps that risk entirely
  // rather than relying on it). vizAudio.crossOrigin='anonymous' (Shopify's
  // CDN sends Access-Control-Allow-Origin: * on these files, confirmed
  // directly) so getByteFrequencyData returns real, non-tainted data.
  function ensureVizAudio() {
    if (vizAudio) { return vizAudio; }
    vizAudio = document.createElement('audio');
    vizAudio.crossOrigin = 'anonymous';
    vizAudio.muted = true; // belt-and-suspenders only -- see comment above on why this isn't load-bearing
    vizAudio.preload = 'none';
    vizAudio.playsInline = true;
    return vizAudio;
  }

  function initAudioGraph() {
    if (audioCtx || !audio) { return; }
    try {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) { return; }
      ensureVizAudio();
      audioCtx = new AC();
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.6;
      freqData = new Uint8Array(analyser.frequencyBinCount);
      sourceNode = audioCtx.createMediaElementSource(vizAudio);
      sourceNode.connect(analyser);
      // No analyser.connect(audioCtx.destination) -- see comment above.
    } catch (e) {
      audioCtx = null;
      analyser = null; // graceful: viz just won't react to bass, arcs stay at rest opacity
    }
    if (audioCtx && audioCtx.state === 'suspended') {
      audioCtx.resume().catch(function () {});
    }
  }

  // Mirrors the REAL element's src/position/play-pause onto the shadow
  // element the analyser taps. Doesn't need frame-perfect sync -- this
  // only feeds a decorative bass-reactive animation -- so a cheap
  // "resync if drifted" check on every tick() (already running every
  // TICK_MS while playing) is enough; no new cross-file events needed,
  // no changes to eva-radio.js's own track-management code at all.
  function syncVizAudio() {
    if (!vizAudio || !audio) { return; }
    var src = audio.currentSrc || audio.src;
    if (src && vizAudio.src !== src) {
      vizAudio.src = src;
      vizAudio.currentTime = audio.currentTime || 0;
    } else if (Math.abs(vizAudio.currentTime - audio.currentTime) > 0.75) {
      vizAudio.currentTime = audio.currentTime; // catches seeks (FF/REW) without needing a dedicated event
    }
    if (audio.paused) {
      if (!vizAudio.paused) { vizAudio.pause(); }
    } else if (vizAudio.paused) {
      var p = vizAudio.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); } // autoplay-policy denial here just means the viz stays inert, same graceful-degradation contract as the rest of this file
    }
  }

  function readBass() {
    if (!analyser) { return 0; }
    analyser.getByteFrequencyData(freqData);
    var sum = 0, n = 0;
    for (var i = 1; i <= 6 && i < freqData.length; i++) { sum += freqData[i]; n++; }
    return n ? (sum / n) / 255 : 0;
  }

  // --- Notes -----------------------------------------------------------

  // originPx (source-pixel space, current angle's own photo) defaults to
  // a random speaker's top edge -- the bass-hit case. Key-press notes
  // (see spawnKeyNote()) pass the pressed key's own position instead.
  function spawnNote(originPx) {
    var geo = currentGeometry();
    if (!geo) { return null; }
    if (!originPx) {
      var speaker = Math.random() < 0.5 ? geo.speakerLeft : geo.speakerRight;
      originPx = { cx: speaker.cx, cy: speaker.cy - geo.speakerR };
    }
    var glyphFn = NOTE_GLYPHS[Math.floor(Math.random() * NOTE_GLYPHS.length)];
    var n = {
      glyphFn: glyphFn,
      originPx: originPx,
      bornAt: Date.now(),
      xJitterFrac: randRange(-0.02, 0.02),
      swayPhase: Math.random() * Math.PI * 2,
      swaySwingCss: randRange(4, 10),
      rotPhase: Math.random() * Math.PI * 2,
      cache: null
    };
    notes.push(n);
    return n;
  }

  function computeNoteCache(n, unit) {
    n.cache = n.glyphFn(0, 0, unit, 1.2);
  }

  function positionForNote(n, W, now) {
    var geo = currentGeometry();
    var srcW = geo ? geo.srcW : 1901;
    var scale = W / srcW;
    var age = now - n.bornAt;
    var t = Math.min(1, age / NOTE_LIFETIME_MS);
    var baseX = (n.originPx.cx + n.xJitterFrac * srcW) * scale;
    var baseY = n.originPx.cy * scale;
    var riseCss = (age / 1000) * NOTE_RISE_PX_PER_S;
    var swayCss = Math.sin(n.swayPhase + age / 1000 * 1.1) * n.swaySwingCss;
    var rotDeg = Math.sin(n.rotPhase + age / 1000 * 0.8) * 10;
    var opacity;
    if (t < 0.12) { opacity = t / 0.12; }
    else if (t > 0.65) { opacity = Math.max(0, (1 - t) / 0.35); }
    else { opacity = 1; }
    return { x: baseX + swayCss, y: baseY - riseCss, rotDeg: rotDeg, opacity: opacity };
  }

  function drawNotes(c, W, now) {
    notes = notes.filter(function (n) { return now - n.bornAt < NOTE_LIFETIME_MS; });
    notes.forEach(function (n) {
      if (!n.cache) { return; }
      var pos = positionForNote(n, W, now);
      c.save();
      c.translate(pos.x, pos.y);
      c.rotate(pos.rotDeg * Math.PI / 180);
      c.globalAlpha = pos.opacity;
      renderGlyph(c, n.cache);
      c.restore();
    });
  }

  // --- Arcs --------------------------------------------------------------

  function computeArcCache(W) {
    var geo = currentGeometry();
    if (!geo) { arcCache = null; return; }
    var scale = W / geo.srcW;
    arcCache = [geo.speakerLeft, geo.speakerRight].map(function (sp) {
      var cx = sp.cx * scale, cy = sp.cy * scale;
      var rings = [];
      for (var i = 0; i < 2; i++) {
        var rr = (geo.speakerR + i * 22) * scale;
        rings.push(ovalPts(cx, cy, rr, rr, 3, 16));
      }
      return rings;
    });
  }

  function drawArcs(c) {
    if (!arcCache) { return; }
    var op = ARC_REST_OPACITY + (ARC_HIT_OPACITY - ARC_REST_OPACITY) * arcPulse;
    c.globalAlpha = op;
    arcCache.forEach(function (rings) {
      rings.forEach(function (pts) {
        c.beginPath();
        strokePointPath(c, pts, true);
        c.stroke();
      });
    });
    c.globalAlpha = 1;
  }

  // --- Hover symbol --------------------------------------------------

  function computeHoverCache(W) {
    if (!hoverAction) { hoverCache = null; return; }
    var geo = currentGeometry();
    var strip = geo && geo.strips[hoverAction];
    if (!strip) { hoverCache = null; return; }
    var scale = W / geo.srcW;
    var fn = HOVER_GLYPHS[hoverAction];
    var g = fn(0, 0, W * 0.03, 1.0);
    g._x = ((strip.x1 + strip.x2) / 2) * scale;
    g._y = geo.hoverY * scale;
    hoverCache = g;
  }

  function drawHover(c) {
    if (!hoverCache) { return; }
    c.save();
    c.translate(hoverCache._x, hoverCache._y);
    c.globalAlpha = 0.85;
    renderGlyph(c, hoverCache);
    c.restore();
  }

  // --- Ticking / render loop -------------------------------------------

  // Hover symbols are independent of playback -- a visitor can hover a
  // button whether or not a tape is playing. Only the speaker arcs/notes
  // are gated on `playing` ("only while playing... vanishes instantly on
  // stop" is specifically about those, not the hover affordance). Both
  // share one canvas/loop; ensureLoopRunning()/maybeStopLoop() keep it
  // alive as long as EITHER is active.
  function tick() {
    if (!canvas) { return; }
    var W = canvas.clientWidth;
    if (!W) { return; }
    computeHoverCache(W);
    // Key-press notes (see spawnKeyNote()) need their wobble/lifetime
    // ticked regardless of `playing` -- only the bass-reactive arcs/new
    // note spawns below are gated on actual tape playback.
    notes.forEach(function (n) { computeNoteCache(n, W * 0.02); });
    if (!playing) { return; }

    syncVizAudio();
    computeArcCache(W);

    var bass = readBass();
    bassSmoothed = bassSmoothed * 0.7 + bass * 0.3;
    arcPulse *= 0.72;
    var now = Date.now();
    if (bass > bassSmoothed * 1.3 && bass > 0.16 && now - lastHitAt > HIT_REFRACTORY_MS) {
      lastHitAt = now;
      arcPulse = 1;
      if (notes.length < MAX_NOTES && Math.random() < 0.45) {
        var n = spawnNote();
        computeNoteCache(n, W * 0.02);
      }
    }
  }

  function render() {
    if (!ctx || !canvas || !currentGeometry() || !(playing || hoverAction || notes.length)) { rafId = null; return; }
    var W = canvas.clientWidth, H = canvas.clientHeight;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    ctx.strokeStyle = ink();
    ctx.fillStyle = ink();
    ctx.lineWidth = 1.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (playing) { drawArcs(ctx); }
    // Notes render independent of playback -- a key press can spawn one
    // whether or not a tape happens to be playing at that moment.
    drawNotes(ctx, W, Date.now());
    drawHover(ctx);

    rafId = requestAnimationFrame(render);
  }

  function ensureLoopRunning() {
    if (!tickTimer) { tickTimer = setInterval(tick, TICK_MS); }
    if (!rafId) { rafId = requestAnimationFrame(render); }
  }

  function forceStopLoop() {
    if (tickTimer) { clearInterval(tickTimer); tickTimer = null; }
    if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    if (ctx && canvas) { ctx.clearRect(0, 0, canvas.width, canvas.height); }
  }

  function maybeStopLoop() {
    if (playing || hoverAction || notes.length) { return; }
    forceStopLoop();
  }

  function startViz() {
    if (REDUCED_MOTION || playing) { return; }
    playing = true;
    initAudioGraph();
    syncVizAudio(); // kick the shadow element off immediately rather than waiting up to TICK_MS for the first tick()
    ensureLoopRunning();
  }

  function stopViz() {
    if (!playing) { return; }
    playing = false;
    notes = [];
    arcPulse = 0;
    if (vizAudio && !vizAudio.paused) { vizAudio.pause(); } // don't leave the shadow element quietly playing (and buffering) once the real tape has stopped
    maybeStopLoop();
  }

  // --- Key-press notes ---------------------------------------------------
  // Public hook for eva-radio-keyboard.js: a note glyph rises from the
  // actually-pressed key's own on-screen position (converted from its
  // live getBoundingClientRect() into the current angle's source-pixel
  // space, the same space every other position in this file works in, so
  // it keeps tracking correctly if the widget gets resized mid-animation)
  // rather than a random speaker. Keys are only ever pressable while
  // their own angle is visible (see eva-radio-keyboard.js), so
  // currentGeometry() is real whenever this actually gets called --
  // still guarded here in case that ever changes.
  function originPxFromRect(rect) {
    var geo = currentGeometry();
    if (!geo || !canvas) { return null; }
    var canvasRect = canvas.getBoundingClientRect();
    if (!canvasRect.width) { return null; }
    var scale = canvasRect.width / geo.srcW;
    return {
      cx: (rect.left + rect.width / 2 - canvasRect.left) / scale,
      cy: (rect.top - canvasRect.top) / scale // rise from just above the key, not its center
    };
  }

  function spawnKeyNote(keyEl) {
    if (REDUCED_MOTION || !keyEl || !canvas || notes.length >= MAX_NOTES) { return; }
    var origin = originPxFromRect(keyEl.getBoundingClientRect());
    if (!origin) { return; }
    var n = spawnNote(origin);
    if (!n) { return; }
    computeNoteCache(n, canvas.clientWidth * 0.02);
    ensureLoopRunning();
  }

  window.EvaRadioViz = { spawnKeyNote: spawnKeyNote };

  // --- Init --------------------------------------------------------------

  ready(function () {
    if (REDUCED_MOTION) { return; }

    // Bass-reactive viz/hover-glyph animation is purely decorative -- a
    // failure here must never take down the radio's own transport
    // (eva-radio.js inits independently), so the whole body runs inside
    // one try/catch.
    try {
      dragHandle = document.getElementById('EvaRadioDragHandle');
      img = dragHandle && dragHandle.querySelector('.eva-radio-widget__img');
      audio = document.getElementById('EvaRadioAudio');
      if (!dragHandle || !img || !audio) { return; }

      canvas = document.createElement('canvas');
      canvas.className = 'eva-radio-widget__viz';
      canvas.setAttribute('aria-hidden', 'true');
      img.insertAdjacentElement('afterend', canvas);
      ctx = canvas.getContext('2d');

      syncCanvasSize();
      if (window.ResizeObserver) {
        new ResizeObserver(syncCanvasSize).observe(img);
      } else {
        window.addEventListener('resize', syncCanvasSize);
      }

      document.addEventListener('eva:radio:playstate', function (e) {
        if (e.detail && e.detail.playing) { startViz(); } else { stopViz(); }
      });

      // eva-radio.js's angle changer -- front and above both have their
      // own GEOMETRY entry; stale arcs/notes drawn for one angle must not
      // linger visually once the visitor cycles to the other.
      document.addEventListener('eva:radio:angle', function (e) {
        currentAngle = (e.detail && e.detail.angle) || 'front';
        if (!currentGeometry()) {
          hoverAction = null;
          hoverCache = null;
          forceStopLoop();
        } else if (playing || hoverAction) {
          ensureLoopRunning();
        }
      });

      // querySelectorAll, not querySelector -- front and above each have
      // their own copy of every data-eva-radio-action polygon (see
      // snippets/eva-radio.liquid), and a singular querySelector would
      // only ever find front's, leaving above's buttons with no hover
      // glyph at all (the same class of bug eva-radio.js's FF/REW
      // binding had before it was fixed the same way).
      Object.keys(HOVER_GLYPHS).forEach(function (action) {
        var els = dragHandle.querySelectorAll('[data-eva-radio-action="' + action + '"]');
        els.forEach(function (el) {
          el.addEventListener('mouseenter', function () {
            hoverAction = action;
            if (canvas) { computeHoverCache(canvas.clientWidth); }
            ensureLoopRunning();
          });
          el.addEventListener('mouseleave', function () {
            if (hoverAction === action) { hoverAction = null; hoverCache = null; }
            maybeStopLoop();
          });
        });
      });
    } catch (vizErr) {
      console.error('[eva-radio-viz] init failed', vizErr);
    }
  });
})();
